// Bucket arithmetic for the top-up-aware usage bars in the manage-plan panels.

type UsageBucketInput = {
  /** Total GAUs used this cycle (plan-pool used + top-up used). */
  used: number;
  /** Total GAUs available this cycle (plan-pool purchased + top-up purchased). */
  total: number;
  /** Total top-up GAUs purchased this cycle. Defaults to 0. */
  topupPurchased?: number;
  /** Top-up GAUs already consumed this cycle. Defaults to 0. */
  topupUsed?: number;
};

/**
 * Decompose `used` / `total` into four buckets and return their ratios.
 * Exported so callers can derive identical numbers (e.g. for a "(N + M)"
 * breakdown label) without re-doing the arithmetic.
 */
export function deriveUsageBuckets({
  used,
  total,
  topupPurchased = 0,
  topupUsed = 0,
}: Pick<UsageBucketInput, "used" | "total" | "topupPurchased" | "topupUsed">) {
  const safeTopupUsed = Math.max(0, Math.min(topupUsed, topupPurchased));
  const safeTopupUnused = Math.max(0, topupPurchased - safeTopupUsed);
  const planTotal = Math.max(0, total - topupPurchased);
  const planUsed = Math.max(0, used - safeTopupUsed);
  const planUsedClamped = Math.min(planUsed, planTotal);
  const planUnused = Math.max(0, planTotal - planUsedClamped);
  return {
    planTotal,
    planUsed: planUsedClamped,
    planUnused,
    topupPurchased,
    topupUsed: safeTopupUsed,
    topupUnused: safeTopupUnused,
  };
}
